<template>
  <div class="flex flex-col gap-4">
    <Checkbox v-model="legacyMode" :label="t('legacyModeLabel')" />

    <template v-if="!legacyMode">
      <div class="flex items-end gap-2">
        <Input
          v-model="couponCode"
          :label="t('couponCode')"
          class="flex-1"
          placeholder="XXXX-XXXX-XXXX"
        />
        <div
          v-if="validationResult"
          class="flex items-center justify-between rounded-md bg-green-50 px-4 py-2"
        >
          <span class="text-sm text-green-700">
            <template v-if="validationResult.type === CouponType.PROMO">
              +{{ validationResult.EXTRA_KM ?? 0 }} km
            </template>
            <template v-else> -<span v-currency="validationResult.deduction" /> </template>
          </span>
        </div>
        <CVButton v-else :disabled="!couponCode || isValidating" @click.prevent="onValidate">
          <Spinner v-if="isValidating" class="size-4" />
          <span v-else>{{ t('validate') }}</span>
        </CVButton>
      </div>

      <div v-if="error" class="text-sm text-red-500">
        {{ t('couponInvalid') }}
      </div>

      <div class="flex items-center justify-end gap-4">
        <CVButton
          variant="default"
          :disabled="!validationResult || isValidating || isUpdating"
          @click.prevent="handleApply"
        >
          <Spinner v-if="isUpdating" class="size-4" />
          <span v-else>{{ t('apply') }}</span>
        </CVButton>
        <CVButton variant="secondary" @click.prevent="emit('cancel')">
          {{ t('cancel') }}
        </CVButton>
      </div>
    </template>

    <template v-else>
      <Input
        v-model="legacyCouponCode"
        :label="t('legacyCouponNumber')"
        class="w-full"
        :placeholder="t('legacyCouponPlaceholder')"
      />
      <Input
        v-model="legacyAmount"
        type="number"
        :min="0.01"
        :max-fraction-digits="2"
        :label="t('legacyAmount')"
        adornment="€"
        class="w-full"
      />

      <div v-if="legacyError" class="text-sm text-red-500">
        {{ t('legacyError') }}
      </div>

      <div class="flex items-center justify-end gap-4">
        <CVButton
          variant="default"
          :disabled="!canSubmitLegacy || isLegacySubmitting"
          @click.prevent="handleLegacySubmit"
        >
          <Spinner v-if="isLegacySubmitting" class="size-4" />
          <span v-else>{{ t('apply') }}</span>
        </CVButton>
        <CVButton variant="secondary" @click.prevent="emit('cancel')">
          {{ t('cancel') }}
        </CVButton>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApplyCoupon, useCreateLegacyCouponPayment } from '@/queries/use-coupons';
import { CouponType } from '@carvia/ros-client-types';
import Checkbox from '../Checkbox.vue';
import CVButton from '../buttons/CVButton.vue';
import Input from '../Input.vue';
import Spinner from '../icons/Spinner.vue';

const props = defineProps<{
  bookingId: string;
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'success'): void;
}>();

const { t } = useI18n();

const legacyMode = ref(false);

const couponCode = ref('');
const error = ref(false);
const validationResult = ref<{
  isValid: boolean;
  type?: CouponType;
  deduction: number;
  EXTRA_KM?: number;
} | null>(null);

const legacyCouponCode = ref('');
const legacyAmount = ref<number | null>(null);
const legacyError = ref(false);

const { mutateAsync: applyCoupon, isPending, variables } = useApplyCoupon();
const { mutateAsync: createLegacyCouponPayment, isPending: isLegacyPending } =
  useCreateLegacyCouponPayment();

const isValidating = computed(() => isPending.value && variables.value?.dryRun === true);
const isUpdating = computed(() => isPending.value && variables.value?.dryRun === false);
const isLegacySubmitting = computed(() => isLegacyPending.value);

const canSubmitLegacy = computed(() => {
  const code = legacyCouponCode.value.trim();
  const amt = legacyAmount.value;
  return code.length > 0 && typeof amt === 'number' && !Number.isNaN(amt) && amt > 0;
});

watch(legacyMode, (legacy) => {
  if (legacy) {
    couponCode.value = '';
    error.value = false;
    validationResult.value = null;
  } else {
    legacyCouponCode.value = '';
    legacyAmount.value = null;
    legacyError.value = false;
  }
});

const onValidate = async () => {
  error.value = false;
  validationResult.value = null;
  if (!couponCode.value) return;

  try {
    const result = await applyCoupon({
      bookingId: props.bookingId,
      couponCode: couponCode.value,
      dryRun: true,
    });
    const isPromo = result.type === CouponType.PROMO;
    if (result.isValid && (isPromo || (result.deduction ?? 0) > 0)) {
      validationResult.value = {
        isValid: true,
        type: result.type,
        deduction: result.deduction ?? 0,
        EXTRA_KM: result.benefits?.EXTRA_KM,
      };
    } else {
      error.value = true;
    }
  } catch {
    error.value = true;
  }
};

watch(couponCode, () => {
  error.value = false;
  validationResult.value = null;
});

const handleApply = async () => {
  if (!validationResult.value) return;

  try {
    await applyCoupon({
      bookingId: props.bookingId,
      couponCode: couponCode.value,
      dryRun: false,
    });
    emit('success');
  } catch {
    error.value = true;
  }
};

const handleLegacySubmit = async () => {
  if (!canSubmitLegacy.value || legacyAmount.value == null) return;
  legacyError.value = false;
  try {
    await createLegacyCouponPayment({
      bookingId: props.bookingId,
      couponCode: legacyCouponCode.value.trim(),
      amount: legacyAmount.value,
    });
    emit('success');
  } catch {
    legacyError.value = true;
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "legacyModeLabel": "Legacy voucher",
    "couponCode": "Coupon code",
    "validate": "Validate",
    "apply": "Apply coupon",
    "cancel": "Cancel",
    "couponInvalid": "Coupon is invalid or has no remaining value",
    "legacyCouponNumber": "Voucher number",
    "legacyCouponPlaceholder": "As printed on the voucher",
    "legacyAmount": "Amount",
    "legacyError": "Could not record the payment. Check amount and permissions."
  },
  "de": {
    "legacyModeLabel": "Legacy-Gutschein",
    "couponCode": "Gutscheincode",
    "validate": "Prüfen",
    "apply": "Gutschein einlösen",
    "cancel": "Abbrechen",
    "couponInvalid": "Gutschein ist ungültig oder hat keinen Restwert",
    "legacyCouponNumber": "Gutscheinnummer",
    "legacyCouponPlaceholder": "Wie auf dem Gutschein angegeben",
    "legacyAmount": "Betrag",
    "legacyError": "Zahlung konnte nicht gebucht werden. Betrag und Berechtigungen prüfen."
  },
  "es": {
    "legacyModeLabel": "Vale legacy",
    "couponCode": "Código de cupón",
    "validate": "Validar",
    "apply": "Aplicar cupón",
    "cancel": "Cancelar",
    "couponInvalid": "El cupón no es válido o no tiene saldo restante",
    "legacyCouponNumber": "Número de vale",
    "legacyCouponPlaceholder": "Como figura en el vale",
    "legacyAmount": "Importe",
    "legacyError": "No se pudo registrar el pago. Compruebe el importe y los permisos."
  }
}
</i18n>
